import Head from 'next/head';
import { useRouter } from 'next/router';
import { generateSchema } from '../../typescript/generateSchema';
import { normLng, parseCoord } from '../mapHelper';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, DEFAULT_SITE_NAME } from './constants';
import { normalizeUrl, getImageUrl, validateSchema } from './utils';
import type { SEOProps } from './types';
import { getCachedAsset } from '../utils';

const siteName = process.env.NEXT_PUBLIC_SITE_NAME || DEFAULT_SITE_NAME;

/**
 * SEO component that generates comprehensive meta tags, Open Graph, Twitter Cards,
 * canonical URLs, hreflang alternates, and structured data (JSON-LD)
 *
 * @param props - SEO configuration object
 * @returns JSX element with all SEO meta tags
 */
export const SEO = ({
  meta_title,
  meta_description,
  keywords,
  share_image,
  currentLocale,
  baseUrl,
  contentType = 'Page',
  publishDate,
  youtube,
  modifiedDate,
  webinarDate,
  authorName = 'Form Technologies',
  streetAddress,
  city,
  region,
  postalCode,
  country,
  latitude,
  longitude,
  telephone,
  availableLocales = [],
  allowedParamsInCanonicalHrefLang,
  robots = 'index, follow'
}: SEOProps) => {
  const { asPath, query } = useRouter();
  const pathSegments = asPath.split('?');
  const cleanPath = pathSegments[0].split('#')[0].replace(/^\/+/, '').replace(/\/$/, '');
  const isDefaultLocale = currentLocale.toLowerCase() === DEFAULT_LOCALE.toLowerCase();

  const rawCanonical = `${baseUrl}${cleanPath ? `/${cleanPath}` : ''}`;
  var canonicalUrl = normalizeUrl(rawCanonical);
  var localeCanonicalUrl = normalizeUrl(`${baseUrl}/${currentLocale.toLowerCase()}/${cleanPath}`);
  var hrefLangSuffix: string = "";

  if (allowedParamsInCanonicalHrefLang && Object.entries(allowedParamsInCanonicalHrefLang).length) {
    const appendParams: string[] = [];

    for (const [k, v] of Object.entries(allowedParamsInCanonicalHrefLang)) {
      if (v)
        appendParams.push(`${k}=${v}`);
    }

    if (appendParams.length) {
      canonicalUrl += `?${appendParams.join('&')}`;
      localeCanonicalUrl += `?${appendParams.join('&')}`;
      hrefLangSuffix = `?${appendParams.join('&')}`;
    }
  }

  const pageUrl = isDefaultLocale ? canonicalUrl : localeCanonicalUrl;
  const imageUrl = getImageUrl(share_image);

  const parsedLatitude = parseCoord(latitude);
  const parsedLongitude = parseCoord(longitude);

  const schema = generateSchema({
    contentType,
    meta_title,
    meta_description,
    share_image,
    publishDate,
    modifiedDate,
    webinarDate,
    authorName,
    canonicalUrl: pageUrl,
    baseUrl,
    streetAddress,
    city,
    region,
    postalCode,
    country,
    latitude: parsedLatitude ?? undefined,
    longitude: parsedLongitude ? normLng(parsedLongitude) : undefined,
    telephone,
  });

  // Validate schema before rendering
  const { isValid, schema: validSchema } = validateSchema(schema);

  return (
    <Head>
      <title>{meta_title || 'Form Technologies'}</title>
      {meta_description && <meta name="description" content={meta_description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <meta httpEquiv="Content-Language" content={currentLocale} />
      {/* Publish date for Algolia Crawler extraction */}
      {publishDate && (
        <>
          <meta name="publish-date" content={publishDate} />
          <meta name="article:published_time" content={publishDate} />
        </>
      )}
      {modifiedDate && <meta name="article:modified_time" content={modifiedDate} />}
      {webinarDate && <meta name="webinar-date" content={webinarDate} />}

      {/* Open Graph */}
      <meta property="og:type" content={contentType} />
      <meta property="og:title" content={meta_title || ''} />
      <meta property="og:description" content={meta_description || ''} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={getCachedAsset(imageUrl, {width: 1200, quality: 75})} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={currentLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta_title || ''} />
      <meta name="twitter:description" content={meta_description || ''} />
      <meta name="twitter:image" content={getCachedAsset(imageUrl, {width: 1200, quality: 75})} />

      {youtube && <meta name="youtube:url" content={youtube} />}

      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />

      {/* hreflang alternates */}
      {SUPPORTED_LOCALES.map(locale => {
        // Only use if current page is available on locale
        if(!availableLocales.length || availableLocales.includes(locale)) {
          let altHref = normalizeUrl(
            cleanPath ? `${baseUrl}/${locale}/${cleanPath}` : `${baseUrl}/${locale}`,
          );
          if(DEFAULT_LOCALE.toLowerCase() == locale.toLowerCase()) {
            altHref = normalizeUrl(
              cleanPath ? `${baseUrl}/${cleanPath}` : `${baseUrl}`,
            );
          }

          if (hrefLangSuffix)
            altHref += hrefLangSuffix;

          return <link key={locale} rel="alternate" href={altHref} hrefLang={locale} />;
        }
      })}

      {/* JSON-LD Structured Data */}
      {validSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(validSchema, null, 0),
          }}
        />
      )}
    </Head>
  );
};
